import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
  Autocomplete,
  CircularProgress
} from "@mui/material";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import BaseURL from "../../api/AppURL";

export default function EditAdmin() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [values, setValues] = useState({
    username: "",
    name: "",
    email: "",
    phone: "",
    address: "",
    password: "",
    roles: null, // role id
  });
  const [roleOptions, setRoleOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  // Snackbar close handler
  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((s) => ({ ...s, open: false }));
  };

  // Input field change handler
  const handleChange = (field) => (e) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
    setErrors((err) => ({ ...err, [field]: null }));
  };

  // Validation
  const validate = () => {
    const err = {};
    if (!values.username.trim()) err.username = "User Name is required";
    if (!values.name.trim()) err.name = "Full Name is required";
    if (!values.email.trim()) err.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(values.email)) err.email = "Invalid email";
    if (!values.phone.trim()) err.phone = "Phone is required";
    if (!values.address.trim()) err.address = "Address is required";
    if (!values.roles) err.roles = "Role is required";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  // Load admin data
  useEffect(() => {
    const fetchData = async () => {
      setFetching(true);
      try {
        const token = localStorage.getItem("auth_token");
        // fetch user info and roles
        const res = await axios.get(
          `${BaseURL}/edit/admin/role/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        if (res.data) {
          if (res.data.roles) setRoleOptions(res.data.roles);
          if (res.data.user) {
            const user = res.data.user;
            setValues({
              username: user.username || "",
              name: user.name || "",
              email: user.email || "",
              phone: user.phone || "",
              address: user.address || "",
              password: "",
              roles: user.roles?.length ? user.roles[0].id : null,
            });
          }
        }
      } catch (err) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load user data",
        });
      } finally {
        setFetching(false);
      }
    };
    if (id) fetchData();
  }, [id]);

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Fix validation errors",
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      // build payload
      const postData = {
        username: values.username,
        name: values.name,
        email: values.email,
        phone: values.phone,
        address: values.address,
        roles: values.roles,
      };
      if (values.password) postData.password = values.password;

      const res = await axios.put(
        `${BaseURL}/admin/user/update/${id}`,
        postData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      setSnackbar({
        open: true,
        severity: "success",
        message: res.data.message || "Admin updated",
      });
      setTimeout(() => {
        navigate("/admin/all_admin");
      }, 1200);
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to update admin";
      setSnackbar({ open: true, severity: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>
          Edit Admin
        </Typography>
        {fetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <TextField
              label="User Name"
              value={values.username}
              onChange={handleChange("username")}
              required
              error={!!errors.username}
              helperText={errors.username}
            />
            <TextField
              label="Full Name"
              value={values.name}
              onChange={handleChange("name")}
              required
              error={!!errors.name}
              helperText={errors.name}
            />
            <TextField
              label="Email"
              value={values.email}
              onChange={handleChange("email")}
              required
              error={!!errors.email}
              helperText={errors.email}
            />
            <TextField
              label="Phone"
              value={values.phone}
              onChange={handleChange("phone")}
              required
              error={!!errors.phone}
              helperText={errors.phone}
            />
            <TextField
              label="Address"
              value={values.address}
              onChange={handleChange("address")}
              required
              error={!!errors.address}
              helperText={errors.address}
              multiline
              rows={2}
            />
            <TextField
              label="Password (leave blank to keep unchanged)"
              type="password"
              value={values.password}
              onChange={handleChange("password")}
            />
            <Autocomplete
              options={roleOptions}
              getOptionLabel={(option) => option.name}
              value={roleOptions.find((r) => r.id === values.roles) || null}
              onChange={(_, newValue) =>
                setValues((v) => ({
                  ...v,
                  roles: newValue ? newValue.id : null,
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign Role"
                  required
                  error={!!errors.roles}
                  helperText={errors.roles}
                />
              )}
            />
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? "Updating..." : "Save Changes"}
            </Button>
          </Box>
        )}
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}